import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type JSX,
} from 'react';
import {
  DROPDOWN_EDGE,
  placeDropdown,
  type DOMRectLike,
  type DropdownPlacement,
} from './placeDropdown';

/**
 * The open menu's BORDER-BOX width, in rem. Before cca-5weu the menu declared
 * `minWidth: '15rem'` with no box-sizing of its own — but every portal that
 * renders it loads a border-box reset (Tailwind's preflight, or @cca/ui/styles),
 * so 15rem was already its whole rendered width; its two items are narrower.
 * Fixed at that, border-box, so the placement's width is the width on screen.
 */
export const SIGN_OUT_MENU_WIDTH_REM = 15;

/** What 1rem is in px (the root font size), or 16 when it cannot be read. */
export function rootFontPx(): number {
  const px = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(px) && px > 0 ? px : 16;
}

/**
 * Where the open menu goes so it is ALWAYS fully on screen (WCAG 2.2 SC 1.4.10,
 * cca-5weu): `placeDropdown`, RIGHT-first. Wherever the old `right: 0`
 * placement fitted inside the screen-edge insets this IS that placement; on a
 * narrow screen where the pill has wrapped to the left of its line, the menu
 * hangs from the pill's left edge instead, or is pulled in from the right edge.
 */
export function placeSignOutMenu(trigger: DOMRectLike, vw: number, remPx = 16): DropdownPlacement {
  return placeDropdown(trigger, vw, { width: SIGN_OUT_MENU_WIDTH_REM * remPx, prefer: 'right' });
}

/** Measure the menu's anchor against the visible viewport (clientWidth: less any
 *  vertical scrollbar). */
function measureMenu(anchor: HTMLElement): DropdownPlacement {
  return placeSignOutMenu(
    anchor.getBoundingClientRect(),
    document.documentElement.clientWidth,
    rootFontPx(),
  );
}

/**
 * Shared sign-out control for every CCAANZ portal. One "Sign out" pill on the
 * coloured header strip; clicking opens a small menu with the two choices —
 * this device, or all devices — so the header isn't cluttered with two buttons.
 *
 * The trigger is the shared `.btn-header` pill, `is-open` while the menu is open —
 * so it takes the header's own focus ring (WCAG 2.2 SC 2.4.7 / 1.4.11), hover
 * wash and open state, like the parish picker beside it (cca-5weu). It carries NO
 * inline style: the class alone sizes and colours it. It does not render
 * standalone — every portal that renders this menu loads @cca/ui/tokens and
 * @cca/ui/components before its own stylesheet (all seven, checked cca-x53l) —
 * and an inline value would beat the class: a background its `:hover` and
 * `.is-open` wash, a colour or border the `--color-on-strip` that keeps the pill
 * white on the band in every theme. The menu panel, which sits on the page rather
 * than the band, is self-styled with token-reading inline styles (with
 * fallbacks), matching the PortalHeader pattern. Both callbacks may be async;
 * each portal passes its own auth plumbing. `onSignOutAll` should carry its own
 * confirmation (the portals already do).
 *
 * The open menu is placed by {@link placeSignOutMenu} — measured as it opens and
 * again on resize — so it stays fully on screen when the header has wrapped the
 * pill to the left of a narrow line (cca-5weu).
 *
 * Keyboard and assistive tech (WCAG 2.2 SC 2.4.3 + 4.1.2, cca-5weu): this is the
 * DISCLOSURE pattern, not an ARIA menu — the trigger carries `aria-expanded` and,
 * while open, `aria-controls` naming the panel; the two choices are plain buttons,
 * reached with Tab in DOM order (no arrow keys: a disclosure does not promise
 * them, and an ARIA `menu` role would). Escape — from the trigger or from inside
 * the panel — and choosing an item close it and put focus back on the trigger; a
 * click elsewhere closes it without taking focus from where the click put it,
 * and so does focus LEAVING it — Tab past the last item, or Shift+Tab out past
 * the trigger (SC 2.4.11, cca-5weu) — so the open panel never covers whatever
 * focus moved on to. Moving between the trigger and the items keeps it open.
 */
export function SignOutMenu({
  onSignOut,
  onSignOutAll,
  label = 'Sign out',
}: {
  onSignOut: () => void | Promise<void>;
  onSignOutAll: () => void | Promise<void>;
  label?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    // Re-measured if the window resizes while the menu is open (a phone rotating).
    const onResize = (): void => {
      if (ref.current) setPlacement(measureMenu(ref.current));
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const menu: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    // Placed by placeSignOutMenu() so it is always on screen; before the first
    // measurement (never, in a browser) the old right-hung default.
    ...(placement
      ? { left: placement.left, width: placement.width }
      : { right: 0, width: `${SIGN_OUT_MENU_WIDTH_REM}rem` }),
    maxWidth: `calc(100vw - ${2 * DROPDOWN_EDGE}px)`,
    // The width INCLUDES the border and padding, or the placement's arithmetic
    // would be 10px short at the screen edge.
    boxSizing: 'border-box',
    background: 'var(--color-panel, #fff)',
    border: '1px solid var(--color-border, #e5e0d5)',
    borderRadius: 'var(--radius-sm, 8px)',
    boxShadow: 'var(--shadow-pop, 0 8px 30px rgba(0, 0, 0, 0.18))',
    padding: 4,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
  };
  const itemBase: CSSProperties = {
    textAlign: 'left',
    background: 'transparent',
    border: 0,
    borderRadius: 'var(--radius-xs, 4px)',
    padding: '9px 10px',
    fontFamily: 'inherit',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--color-ink, #111827)',
    cursor: 'pointer',
  };
  const sub: CSSProperties = {
    display: 'block',
    fontSize: 11.5,
    fontWeight: 400,
    color: 'var(--color-muted, #6b7280)',
    marginTop: 2,
  };

  const act = (fn: () => void | Promise<void>): void => {
    setOpen(false);
    // Focus goes back to the trigger BEFORE the action runs, so a confirm dialog
    // the action opens returns focus there too, not to a button that is gone.
    triggerRef.current?.focus();
    void fn();
  };
  const item = (i: number): CSSProperties => ({
    ...itemBase,
    background: hover === i ? 'var(--color-structural-wash, #f5f0e8)' : 'transparent',
  });
  // Focus moving OUT of the control closes it — the click-elsewhere rule, and like
  // it, focus stays where it went. Between the trigger and the items it is still
  // inside, so the menu stays open. A null relatedTarget (the window losing focus,
  // a click on a non-focusable spot, or Safari's click on a button, which never
  // focuses it) does NOT close it: closing there would unmount an item between its
  // mousedown and its click.
  const closeOnFocusLeave = (e: ReactFocusEvent<HTMLDivElement>): void => {
    const next = e.relatedTarget;
    if (next !== null && !e.currentTarget.contains(next)) setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }} onBlur={closeOnFocusLeave}>
      <button
        ref={triggerRef}
        type="button"
        className={`btn-header${open ? ' is-open' : ''}`}
        aria-expanded={open}
        // Names the panel only while it exists — a reference to an absent id
        // is an invalid IDREF.
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          // Measured as it opens, so its first paint is already on screen.
          if (!open && ref.current) setPlacement(measureMenu(ref.current));
          setOpen((o) => !o);
        }}
      >
        {label}
        {/* Turns while open, as the parish picker's chevron does. */}
        <span
          aria-hidden="true"
          style={{
            fontSize: 9,
            opacity: 0.85,
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div id={panelId} style={menu}>
          <button
            type="button"
            style={item(0)}
            onMouseEnter={() => setHover(0)}
            onMouseLeave={() => setHover(null)}
            onClick={() => act(onSignOut)}
          >
            Sign out
            <span style={sub}>This device only</span>
          </button>
          <button
            type="button"
            style={item(1)}
            onMouseEnter={() => setHover(1)}
            onMouseLeave={() => setHover(null)}
            onClick={() => act(onSignOutAll)}
          >
            Sign out all devices
            <span style={sub}>Ends every session, everywhere</span>
          </button>
        </div>
      )}
    </div>
  );
}
